import { LitElement, html, css } from 'lit'
import { property, customElement } from 'lit/decorators.js'
import { shouldShowInsightSurvey } from './insight-hub-utils'
import { AuthService } from '../../../../../src/services/auth/auth-service'
import { InsightHubService } from '../../../../../src/services/insight-hub-service'
import { isOnline } from '../../../../../src/utils/helpers'

@customElement('insight-hub-button')
export class InsightHubButton extends LitElement {
    @property({ type: String, attribute: 'popup-id' })
    popupId: string = ''

    @property({ type: String, attribute: 'storage-key' })
    storageKey: string = ''

    @property({ type: Number, attribute: 'months-interval' })
    monthsInterval: number = 1

    @property({ type: String, attribute: 'start-date' }) startDate:
        | string
        | null = null

    private _realPopupButton: HTMLElement | null = null
    private static _scriptLoaded = false
    private _handleSubmitCallback: string = ''
    private _buttonDisabled: boolean = true
    private _username = AuthService.getSessionUsername();

    static styles = css`
        :host {
            display: inline-block;
            height: 24px;
        }

        button {
            background: none;
            color: inherit;
            border: none;
            padding: 0;
            font: inherit;
            cursor: pointer;
            outline: inherit;
        }

        button:disabled {
            opacity: 0.7;
        }
    `

    connectedCallback() {
        super.connectedCallback()
        this._createOrUpdatePopupButton()
        this._ensurePopupScriptLoaded()

        // small delay for the script to initialise
        setTimeout(() => this._updateButtonState(), 500)
    }

    disconnectedCallback() {
        super.disconnectedCallback()
    }

    render() {
        return html`
            <mc-tooltip>
                <button
                    @click="${this._triggerPopup}"
                    .disabled=${this._buttonDisabled}
                    slot="trigger"
                >
                    <slot></slot>
                </button>
                ${!isOnline()
                    ? html`<span>Unavailable when offline</span>`
                    : this._buttonDisabled
                    ? html`<span>Feedback already provided</span>`
                    : null}
            </mc-tooltip>
        `
    }

    private async _updateButtonState() {
        const insightsHub = await InsightHubService.getLatestFeedback(this._username || '', this.popupId);
        const shouldShow = shouldShowInsightSurvey(
            insightsHub?.created_at || null,
            this.startDate || '',
            this.monthsInterval,
        )

        this._buttonDisabled = !shouldShow
        this.requestUpdate()

        if (shouldShow) {
            this._handlePeriodicPopup()
        }
    }

    private _createOrUpdatePopupButton() {
        // checking if button already exists
        let popupButton = document.querySelector(
            `button[data-anchor-popup="${this.popupId}"]`,
        ) as HTMLElement

        if (!popupButton) {
            this._createSubmitCallback()

            // creating the button
            popupButton = document.createElement('button')
            popupButton.id = `real-popup-button-${this.popupId.slice(0, 8)}`
            popupButton.setAttribute('data-anchor-popup', this.popupId)
            this._username && popupButton.setAttribute('data-anchor-user-email', this._username)
            popupButton.setAttribute(
                'data-anchor-on-submit',
                this._handleSubmitCallback,
            )

            // hiding it (but keeping it functional)
            popupButton.style.position = 'fixed'
            popupButton.style.opacity = '0'
            popupButton.style.pointerEvents = 'none'
            popupButton.style.top = '-1000px'
            popupButton.style.left = '-1000px'

            document.body.appendChild(popupButton)
        }

        this._realPopupButton = popupButton
    }

    private _createSubmitCallback() {
        const functionName = `insightHubSubmit_${this.popupId.replace(
            /[^a-zA-Z0-9]/g,
            '',
        )}`
        this._handleSubmitCallback = functionName

        // global scope
        ;(window as any)[functionName] = () => {
            const now = new Date()
            this._buttonDisabled = true
            this.requestUpdate()
        }
    }

    private _ensurePopupScriptLoaded() {
        // only load the script once across all instances
        if (InsightHubButton._scriptLoaded) return

        // checking if script is already loaded
        if (!document.querySelector('script[src*="embed.js"]')) {
            const script = document.createElement('script')
            script.type = 'module'
            script.src = 'https://insightshub.maersk-digital.net/embed/embed.js'

            document.head.appendChild(script)

            InsightHubButton._scriptLoaded = true
        } else {
            InsightHubButton._scriptLoaded = true
        }
    }

    private _triggerPopup() {
        if (this._realPopupButton) {
            this._realPopupButton.click()
        }
    }

    private _handlePeriodicPopup() {
        if (!this._realPopupButton) return

        this._realPopupButton.setAttribute('open', 'load')

        // triggering after a short delay to ensure everything is loaded
        setTimeout(() => {
            if (this._realPopupButton) {
                this._realPopupButton.click()
            }
        }, 1000)
    }
}
